import { TFunction } from 'react-i18next';
import { Formik, FormikProps } from 'formik';

import Grid from '@mui/material/Grid';

import Box from '@mui/material/Box';
import {
  AutocompleteField,
  CheckboxField,
  SelectField,
  TextField,
  DateField,
} from '../../componentsv2/Field';
import MultiSelectField from '../../componentsv2/Field/MultiSelectField';
import Button from '../../componentsv2/Button';
import Form from '../../componentsv2/Form';
import DateRangeField from '../../componentsv2/Field/DateRangeField';
import RangeField, { RangeFieldTypes } from '../../componentsv2/Field/RangeField';

import { useTranslation } from '../../hooks/i18n';
import { FilterType, Nullable } from '../../types';
import { COLORS } from '../../themev2';

type TableFiltersProps = {
  filterOptions: FilterType[];
  applyFilters: Nullable<Function>;
  clearFilters: Nullable<Function>;
  initialValues: any;
  trackingEvent?: string;
  showFilteringButtons?: boolean;
  showLabel?: boolean;
};

const renderFilterFormikField = (
  {
    name,
    type,
    title,
    placeholder = 'admin:clientsPage.gridFilters.genericFilterPlaceholder',
    options = [],
    optionsWithLabel,
    tKey,
    Icon,
    showLabel = true,
    disableFuture = true,
    clearable = true,
    maxDifference,
    minDate,
    onInputChange,
    debounceInputChange,
    isLoading,
  }: FilterType,
  values: any,
  t: TFunction,
  setFieldValue: (field: string, value: any) => void
) => {
  if (type === 'text') {
    return (
      <TextField
        startAdornment={Icon}
        name={name}
        placeholder={t(placeholder)}
        label={showLabel ? (title ? t(title) : '') : undefined}
        clearable
      />
    );
  } else if (type === 'number') {
    return (
      <TextField
        type="number"
        startAdornment={Icon}
        name={name}
        placeholder={t(placeholder)}
        label={showLabel ? (title ? t(title) : '') : undefined}
        clearable
      />
    );
  } else if (type === 'select') {
    return (
      <SelectField
        startAdornment={Icon}
        name={name}
        placeholder={t(placeholder)}
        label={showLabel ? (title ? t(title) : '') : undefined}
        clearable
        options={
          optionsWithLabel ||
          options.map((value: string) => {
            // if tKey is a function, it means that we need to use it to get the label, this is useful for translation keys that are more complex than just a string
            const label = tKey && typeof tKey === 'function' ? tKey(value) : `${tKey}.${value}`;
            return {
              value,
              label: tKey ? t(label) : value,
            };
          })
        }
      />
    );
  } else if (type === 'autocomplete') {
    return (
      <AutocompleteField
        inputStartAdornment={
          Icon ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: COLORS.gray[1200],
                pl: '8px',
                pr: '4px',
              }}
            >
              {Icon}
            </Box>
          ) : null
        }
        name={name}
        placeholder={t(placeholder)}
        label={showLabel ? (title ? t(title) : '') : undefined}
        clearable
        onInputChange={onInputChange}
        debounceInputChange={debounceInputChange}
        isLoading={isLoading}
        options={
          optionsWithLabel ||
          options.map((value: string) => ({
            value,
            label: tKey && typeof tKey === 'function' ? t(tKey(value)) : value,
          }))
        }
      />
    );
  } else if (type === 'multipleSelect') {
    return (
      <MultiSelectField
        startAdornment={Icon}
        name={name}
        value={values[name] ?? []}
        placeholder={t(placeholder)}
        label={showLabel ? (title ? t(title) : '') : undefined}
        options={options.map((value: string) => ({
          value,
          label: tKey ? t(`${tKey}.${value}`) : value,
        }))}
        sx={{
          '& .MuiOutlinedInput-input': {},
          '& fieldset': {
            borderColor: COLORS.gray[900],
            borderRadius: '8px',
          },
        }}
      />
    );
  } else if (type === 'checkbox') {
    return <CheckboxField name={name} label={showLabel ? (title ? t(title) : '') : undefined} />;
  } else if (type === 'dateRange') {
    return (
      <DateRangeField
        name={name}
        label={showLabel ? (title ? t(title) : '') : undefined}
        disableFuture={disableFuture}
        clearable
        value={values[name]}
      />
    );
  } else if (type === 'numberRange') {
    return (
      <RangeField
        name={name}
        type={RangeFieldTypes.NUMBER}
        startAdornment={Icon}
        placeholder={t(placeholder)}
        label={showLabel ? (title ? t(title) : '') : undefined}
        value={values[name]}
        clearable
        fullWidth
      />
    );
  } else if (type === 'dateRangeSingleField') {
    return (
      <RangeField
        name={name}
        type={RangeFieldTypes.DATE}
        startAdornment={Icon}
        placeholder={t(placeholder)}
        label={showLabel ? (title ? t(title) : '') : undefined}
        value={values[name]}
        clearable={clearable}
        disableFuture={disableFuture}
        maxDifference={maxDifference}
        minDate={minDate}
        fullWidth
      />
    );
  } else if (type === 'month') {
    const labelText = showLabel
      ? title
        ? t(title)
        : placeholder
        ? t(placeholder)
        : ''
      : '';
    return (
      <DateField
        name={name}
        label={labelText}
        onlyMonthYear
        disableFuture={disableFuture}
        minDate={minDate}
      />
    );
  }
};

function FiltersForm({
  filterOptions,
  trackingEvent,
  values,
  t,
  setFieldValue,
  resetForm,
  clearFilters,
  showFilteringButtons = true,
  submitForm,
  dirty,
}: {
  filterOptions: FilterType[];
  values: any;
  t: TFunction;
  setFieldValue: (field: string, value: any) => void;
  resetForm: () => void;
  clearFilters: Nullable<Function>;
  trackingEvent?: string;
  showFilteringButtons?: boolean;
  submitForm: () => void;
  dirty: boolean;
}) {
  return (
    <Form
      name="tableFilters"
      id="tableFilters"
      noValidate
      style={{ width: '100%' }}
      trackingEvent={trackingEvent}
    >
      <Grid container rowSpacing={2} columnSpacing={2} sx={{ alignItems: 'end' }}>
        {filterOptions.map((filter: FilterType) => (
          <Grid item xs={filter.size} key={filter.name} style={{ display: 'flex' }}>
            {renderFilterFormikField(filter, values, t, setFieldValue)}
          </Grid>
        ))}
        {showFilteringButtons ? (
          <>
            <Grid item xs={1}>
              <Button
                fullWidth
                onClick={() => {
                  resetForm();
                  clearFilters && clearFilters();
                }}
                type="outline"
              >
                {t(`admin:clientsPage.gridFilters.reset`)}
              </Button>
            </Grid>
            <Grid item xs={1}>
              <Button isSubmit fullWidth>
                {t(`admin:clientsPage.gridFilters.apply`)}
              </Button>
            </Grid>
          </>
        ) : (
          <Grid item xs={1}>
            <Button isSubmit fullWidth disabled={!dirty} color="secondary">
              {t(`admin:clientsPage.gridFilters.apply`)}
            </Button>
          </Grid>
        )}
      </Grid>
    </Form>
  );
}

const TableFilters = ({
  filterOptions = [],
  applyFilters,
  clearFilters,
  initialValues,
  trackingEvent,
  showFilteringButtons = true,
}: TableFiltersProps) => {
  const { t } = useTranslation('admin');

  return (
    <Formik
      initialValues={{ ...initialValues }}
      enableReinitialize
      onSubmit={(values: any) => {
        applyFilters && applyFilters(values);
      }}
    >
      {({ values, resetForm, setFieldValue, submitForm, dirty }: FormikProps<any>) => {
        return (
          <FiltersForm
            filterOptions={filterOptions}
            values={values}
            t={t}
            setFieldValue={setFieldValue}
            resetForm={resetForm}
            clearFilters={clearFilters}
            trackingEvent={trackingEvent}
            showFilteringButtons={showFilteringButtons}
            submitForm={submitForm}
            dirty={dirty}
          />
        );
      }}
    </Formik>
  );
};

export default TableFilters;
